/**
 * One-time migration: create default tenant and copy root data into tenants/{tenantId}/...
 * Run from Super Admin dashboard when moving to multi-tenant.
 */
import { ref, get, set } from 'firebase/database';
import { db } from './config';
import { createTenant, getTenantBySlug } from './tenants';

const DEFAULT_SLUG = 'demusclefactory';
const DEFAULT_NAME = 'De Muscle Factory';

export async function runMigration() {
  try {
    // 1. Get or create default tenant
    let tenantId;
    const existing = await getTenantBySlug(DEFAULT_SLUG);
    if (existing.success && existing.tenant) {
      tenantId = existing.tenant.id;
    } else {
      const created = await createTenant({
        name: DEFAULT_NAME,
        slug: DEFAULT_SLUG,
        status: 'active',
        branding: {
          siteName: DEFAULT_NAME,
          logoUrl: '',
          primaryColor: '#1a1a2e',
          accentColor: '#e94560'
        },
        features: {
          contactFormEnabled: true,
          showClasses: true,
          showTrainers: true
        }
      });
      if (!created.success) {
        return { success: false, message: created.message || 'Failed to create tenant' };
      }
      tenantId = created.tenantId;
    }

    const base = `tenants/${tenantId}`;
    let copied = { members: 0, payments: 0, contacts: 0, reminders: 0 };

    // 2. Copy members
    const membersSnap = await get(ref(db, 'members'));
    if (membersSnap.exists()) {
      await set(ref(db, `${base}/members`), membersSnap.val());
      copied.members = Object.keys(membersSnap.val()).length;
    }

    // 3. Copy payments
    const paymentsSnap = await get(ref(db, 'payments'));
    if (paymentsSnap.exists()) {
      await set(ref(db, `${base}/payments`), paymentsSnap.val());
      copied.payments = Object.keys(paymentsSnap.val()).length;
    }

    // 4. Copy contacts
    const contactsSnap = await get(ref(db, 'contacts'));
    if (contactsSnap.exists()) {
      await set(ref(db, `${base}/contacts`), contactsSnap.val());
      copied.contacts = Object.keys(contactsSnap.val()).length;
    }

    // 5. Copy reminders
    const remindersSnap = await get(ref(db, 'reminders'));
    if (remindersSnap.exists()) {
      await set(ref(db, `${base}/reminders`), remindersSnap.val());
      copied.reminders = Object.keys(remindersSnap.val()).length;
    }

    return {
      success: true,
      tenantId,
      slug: DEFAULT_SLUG,
      message: 'Migration completed. Existing data copied to default tenant.',
      copied
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Migration failed'
    };
  }
}
